// frontend/src/modules/relatorios/pages/Relatorios.js
import React, { useState, useEffect } from 'react';
import { FaFilePdf, FaFileExcel, FaPrint, FaFilter, FaTimes } from 'react-icons/fa';
import { exportToPDF, exportToExcel, exportDREtoPDF } from '../../../services/exportService';
import { useMediaQuery } from 'react-responsive';

function Relatorios({ usuario }) {
  const empresaId = usuario?.empresa_id || 1;
  const [dados, setDados] = useState({
    receitas: 0,
    despesas: 0,
    saldo: 0,
    lancamentos: [],
    resumoPorCategoria: {}
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [periodo, setPeriodo] = useState({ 
    mes: new Date().getMonth() + 1, 
    ano: new Date().getFullYear() 
  });
  const [filtros, setFiltros] = useState({
    tipo: '',
    status: ''
  });
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  const isMobile = useMediaQuery({ maxWidth: 768 });
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchDados();
  }, [periodo, empresaId, filtros]);

  const fetchDados = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/lancamentos/empresa/${empresaId}`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erro:', errorText);
        throw new Error(`Erro ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('📦 Dados recebidos:', data.length, 'lançamentos');
      
      let dadosFiltrados = data.filter(item => {
        const date = new Date(item.data_lancamento);
        return date.getMonth() + 1 === periodo.mes && date.getFullYear() === periodo.ano;
      });

      if (filtros.tipo) {
        dadosFiltrados = dadosFiltrados.filter(item => item.tipo === filtros.tipo);
      }

      if (filtros.status) {
        dadosFiltrados = dadosFiltrados.filter(item => item.status === filtros.status);
      }

      let receitas = 0;
      let despesas = 0;
      const resumoPorCategoria = {};

      dadosFiltrados.forEach(item => {
        const valor = Number(item.valor) || 0;
        if (item.tipo === 'RECEITA') {
          receitas += valor;
        } else if (item.tipo === 'DESPESA') {
          despesas += valor;
        }

        const categoria = item.categoria || 'Sem categoria';
        if (!resumoPorCategoria[categoria]) {
          resumoPorCategoria[categoria] = { receitas: 0, despesas: 0 };
        }
        if (item.tipo === 'RECEITA') {
          resumoPorCategoria[categoria].receitas += valor;
        } else {
          resumoPorCategoria[categoria].despesas += valor;
        }
      });

      setDados({
        receitas,
        despesas,
        saldo: receitas - despesas,
        lancamentos: dadosFiltrados,
        resumoPorCategoria
      });
    } catch (err) {
      console.error('❌ Erro detalhado:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  if (loading) return <div style={{ textAlign: 'center', padding: '50px' }}>🔄 Carregando dados...</div>;
  
  if (error) return (
    <div style={{ textAlign: 'center', padding: '50px' }}>
      <div style={{ fontSize: '48px', marginBottom: '10px' }}>❌</div>
      <p style={{ color: 'red', fontSize: isMobile ? '14px' : '16px' }}>{error}</p>
      <button 
        onClick={fetchDados}
        style={{ 
          marginTop: '10px', 
          padding: isMobile ? '8px 20px' : '10px 20px', 
          backgroundColor: '#007bff', 
          color: 'white', 
          border: 'none', 
          borderRadius: '4px', 
          cursor: 'pointer',
          fontSize: isMobile ? '13px' : '14px',
          width: isMobile ? '100%' : 'auto'
        }}
      >
        Tentar Novamente
      </button>
    </div>
  );

  return (
    <div style={{ padding: isMobile ? '10px' : '20px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Cabeçalho */}
      <div style={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center', 
        marginBottom: '20px',
        flexWrap: 'wrap',
        gap: '10px'
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: isMobile ? '20px' : '28px' }}>📄 Relatórios Financeiros</h2>
          <p style={{ color: '#6c757d', margin: '5px 0 0 0', fontSize: isMobile ? '13px' : '14px' }}>
            Análise completa das receitas e despesas
          </p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          <select 
            value={periodo.mes} 
            onChange={(e) => setPeriodo({ ...periodo, mes: parseInt(e.target.value) })} 
            style={{ 
              padding: isMobile ? '6px 10px' : '8px 12px', 
              borderRadius: '4px', 
              border: '1px solid #ddd',
              fontSize: isMobile ? '12px' : '14px',
              flex: isMobile ? '1' : '0'
            }}
          >
            {meses.map((m, i) => (<option key={i + 1} value={i + 1}>{m}</option>))}
          </select>
          <select 
            value={periodo.ano} 
            onChange={(e) => setPeriodo({ ...periodo, ano: parseInt(e.target.value) })} 
            style={{ 
              padding: isMobile ? '6px 10px' : '8px 12px', 
              borderRadius: '4px', 
              border: '1px solid #ddd',
              fontSize: isMobile ? '12px' : '14px',
              flex: isMobile ? '1' : '0'
            }}
          >
            {[2024, 2025, 2026, 2027].map(a => (<option key={a} value={a}>{a}</option>))}
          </select>
          <button 
            onClick={() => setMostrarFiltros(!mostrarFiltros)}
            style={{ 
              padding: isMobile ? '6px 12px' : '8px 16px', 
              backgroundColor: mostrarFiltros ? '#dc3545' : '#6c757d',
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              fontSize: isMobile ? '12px' : '14px',
              flex: isMobile ? '1' : '0',
              justifyContent: 'center'
            }}
          >
            {mostrarFiltros ? <FaTimes /> : <FaFilter />} 
            {mostrarFiltros ? 'Fechar' : 'Filtros'}
          </button>
          <button 
            onClick={() => exportToPDF(dados.lancamentos, 'Relatorio_Lancamentos')} 
            style={{ 
              padding: isMobile ? '6px 12px' : '8px 16px', 
              backgroundColor: '#dc3545', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '5px',
              fontSize: isMobile ? '12px' : '14px',
              flex: isMobile ? '1' : '0',
              justifyContent: 'center'
            }}
          >
            <FaFilePdf /> PDF
          </button>
          <button 
            onClick={() => exportToExcel(dados.lancamentos, 'Relatorio_Lancamentos')} 
            style={{ 
              padding: isMobile ? '6px 12px' : '8px 16px', 
              backgroundColor: '#28a745', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '5px',
              fontSize: isMobile ? '12px' : '14px',
              flex: isMobile ? '1' : '0',
              justifyContent: 'center'
            }}
          >
            <FaFileExcel /> Excel
          </button>
          <button 
            onClick={() => exportDREtoPDF({ receitas: dados.receitas, despesas: dados.despesas, saldo: dados.saldo }, `${meses[periodo.mes-1]}/${periodo.ano}`)} 
            style={{ 
              padding: isMobile ? '6px 12px' : '8px 16px', 
              backgroundColor: '#007bff', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer', 
              display: 'flex', 
              alignItems: 'center', 
              gap: '5px',
              fontSize: isMobile ? '12px' : '14px',
              flex: isMobile ? '1' : '0',
              justifyContent: 'center'
            }}
          >
            <FaPrint /> DRE PDF
          </button>
        </div>
      </div>

      {/* Filtros Avançados */}
      {mostrarFiltros && (
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: isMobile ? '12px 15px' : '15px 20px', 
          borderRadius: '8px', 
          marginBottom: '20px',
          border: '1px solid #dee2e6'
        }}>
          <h4 style={{ margin: '0 0 15px 0', fontSize: isMobile ? '14px' : '16px' }}>🔍 Filtros Avançados</h4>
          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
            <div style={{ width: isMobile ? '100%' : 'auto' }}>
              <label style={{ marginRight: '5px', fontSize: isMobile ? '13px' : '14px' }}>Tipo:</label>
              <select 
                value={filtros.tipo} 
                onChange={(e) => setFiltros({...filtros, tipo: e.target.value})}
                style={{ 
                  padding: '6px 12px', 
                  borderRadius: '4px', 
                  border: '1px solid #ddd',
                  width: isMobile ? '100%' : 'auto',
                  fontSize: isMobile ? '13px' : '14px'
                }}
              >
                <option value="">Todos</option>
                <option value="RECEITA">💰 Receitas</option>
                <option value="DESPESA">💸 Despesas</option>
              </select>
            </div>
            <div style={{ width: isMobile ? '100%' : 'auto' }}>
              <label style={{ marginRight: '5px', fontSize: isMobile ? '13px' : '14px' }}>Status:</label>
              <select 
                value={filtros.status} 
                onChange={(e) => setFiltros({...filtros, status: e.target.value})}
                style={{ 
                  padding: '6px 12px', 
                  borderRadius: '4px', 
                  border: '1px solid #ddd',
                  width: isMobile ? '100%' : 'auto',
                  fontSize: isMobile ? '13px' : '14px'
                }}
              >
                <option value="">Todos</option>
                <option value="PENDENTE">⏳ Pendente</option>
                <option value="PAGO">✅ Pago</option>
                <option value="VENCIDO">❌ Vencido</option>
              </select>
            </div>
            <button 
              onClick={() => { setFiltros({ tipo: '', status: '' }); }}
              style={{ 
                padding: isMobile ? '6px 12px' : '6px 16px', 
                backgroundColor: '#6c757d', 
                color: 'white', 
                border: 'none', 
                borderRadius: '4px', 
                cursor: 'pointer',
                fontSize: isMobile ? '12px' : '14px',
                width: isMobile ? '100%' : 'auto'
              }}
            >
              Limpar Filtros
            </button>
          </div>
        </div>
      )}

      {/* Cards de Resumo */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', 
        gap: isMobile ? '10px' : '20px', 
        marginBottom: '30px' 
      }}>
        <div style={{ backgroundColor: '#d4edda', padding: isMobile ? '15px' : '20px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
          <h3 style={{ margin: 0, color: '#155724', fontSize: isMobile ? '14px' : '18px' }}>💰 Receitas</h3>
          <p style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 'bold', margin: '10px 0 0 0', color: '#155724' }}>
            {formatCurrency(dados.receitas)}
          </p>
          <small style={{ color: '#155724', fontSize: isMobile ? '11px' : '12px' }}>Total do período</small>
        </div>
        <div style={{ backgroundColor: '#f8d7da', padding: isMobile ? '15px' : '20px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
          <h3 style={{ margin: 0, color: '#721c24', fontSize: isMobile ? '14px' : '18px' }}>💸 Despesas</h3>
          <p style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 'bold', margin: '10px 0 0 0', color: '#721c24' }}>
            {formatCurrency(dados.despesas)}
          </p>
          <small style={{ color: '#721c24', fontSize: isMobile ? '11px' : '12px' }}>Total do período</small>
        </div>
        <div style={{ backgroundColor: '#cce5ff', padding: isMobile ? '15px' : '20px', borderRadius: '8px', textAlign: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
          <h3 style={{ margin: 0, color: '#004085', fontSize: isMobile ? '14px' : '18px' }}>📈 Saldo</h3>
          <p style={{ fontSize: isMobile ? '22px' : '28px', fontWeight: 'bold', margin: '10px 0 0 0', color: '#004085' }}>
            {formatCurrency(dados.saldo)}
          </p>
          <small style={{ color: '#004085', fontSize: isMobile ? '11px' : '12px' }}>Resultado do período</small>
        </div>
      </div>

      {/* DRE - Demonstração do Resultado */}
      <div style={{ backgroundColor: 'white', padding: isMobile ? '15px' : '20px', borderRadius: '8px', marginBottom: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', overflowX: 'auto' }}>
        <h3 style={{ fontSize: isMobile ? '16px' : '18px' }}>📊 DRE - Demonstração do Resultado</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td style={{ padding: isMobile ? '6px 8px' : '8px', borderBottom: '1px solid #dee2e6', fontSize: isMobile ? '13px' : '14px' }}>Receita Total</td>
              <td style={{ padding: isMobile ? '6px 8px' : '8px', borderBottom: '1px solid #dee2e6', textAlign: 'right', color: '#28a745', fontWeight: 'bold', fontSize: isMobile ? '13px' : '14px' }}>
                {formatCurrency(dados.receitas)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: isMobile ? '6px 8px' : '8px', borderBottom: '1px solid #dee2e6', fontSize: isMobile ? '13px' : '14px' }}>(-) Despesas Totais</td>
              <td style={{ padding: isMobile ? '6px 8px' : '8px', borderBottom: '1px solid #dee2e6', textAlign: 'right', color: '#dc3545', fontWeight: 'bold', fontSize: isMobile ? '13px' : '14px' }}>
                {formatCurrency(dados.despesas)}
              </td>
            </tr>
            <tr>
              <td style={{ padding: isMobile ? '6px 8px' : '8px', fontWeight: 'bold', fontSize: isMobile ? '16px' : '18px', borderTop: '2px solid #dee2e6' }}>= Resultado</td>
              <td style={{ padding: isMobile ? '6px 8px' : '8px', textAlign: 'right', fontWeight: 'bold', fontSize: isMobile ? '16px' : '18px', color: dados.saldo >= 0 ? '#28a745' : '#dc3545', borderTop: '2px solid #dee2e6' }}>
                {formatCurrency(dados.saldo)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Resumo por Categoria */}
      <div style={{ backgroundColor: 'white', padding: isMobile ? '15px' : '20px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
        <h3 style={{ fontSize: isMobile ? '16px' : '18px' }}>📊 Resumo por Categoria</h3>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: isMobile ? '8px' : '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: isMobile ? '12px' : '14px' }}>Categoria</th>
                <th style={{ padding: isMobile ? '8px' : '10px', textAlign: 'right', borderBottom: '2px solid #dee2e6', fontSize: isMobile ? '12px' : '14px' }}>Receitas</th>
                <th style={{ padding: isMobile ? '8px' : '10px', textAlign: 'right', borderBottom: '2px solid #dee2e6', fontSize: isMobile ? '12px' : '14px' }}>Despesas</th>
                <th style={{ padding: isMobile ? '8px' : '10px', textAlign: 'right', borderBottom: '2px solid #dee2e6', fontSize: isMobile ? '12px' : '14px' }}>Saldo</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(dados.resumoPorCategoria).length === 0 ? (
                <tr>
                  <td colSpan="4" style={{ textAlign: 'center', padding: '20px', fontSize: isMobile ? '13px' : '14px' }}>
                    📭 Nenhuma categoria encontrada
                  </td>
                </tr>
              ) : (
                Object.entries(dados.resumoPorCategoria).map(([categoria, values]) => (
                  <tr key={categoria} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: isMobile ? '8px' : '10px', fontSize: isMobile ? '12px' : '14px' }}>{categoria}</td>
                    <td style={{ padding: isMobile ? '8px' : '10px', textAlign: 'right', color: '#28a745', fontSize: isMobile ? '12px' : '14px' }}>
                      {formatCurrency(values.receitas)}
                    </td>
                    <td style={{ padding: isMobile ? '8px' : '10px', textAlign: 'right', color: '#dc3545', fontSize: isMobile ? '12px' : '14px' }}>
                      {formatCurrency(values.despesas)}
                    </td>
                    <td style={{ padding: isMobile ? '8px' : '10px', textAlign: 'right', fontWeight: 'bold', fontSize: isMobile ? '12px' : '14px' }}>
                      {formatCurrency(values.receitas - values.despesas)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {Object.entries(dados.resumoPorCategoria).length > 0 && (
          <div style={{ 
            marginTop: '15px', 
            paddingTop: '15px', 
            borderTop: '2px solid #dee2e6',
            display: 'flex',
            justifyContent: 'flex-end',
            fontWeight: 'bold',
            fontSize: isMobile ? '14px' : '16px'
          }}>
            <span style={{ marginRight: '30px' }}>
              Total Geral: {formatCurrency(dados.saldo)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default Relatorios;